import { useFetch } from "../useFetch";
import type {
	ServiceProviderResponse,
	CreateServiceProviderPayload,
	UpdateServiceProviderPayload,
	ToggleServiceStatusPayload,
} from "../../core/types/services";

// ============================================
// GET SERVICE BY ID
// ============================================
export function useServiceProvider(id: number) {
	return useFetch<ServiceProviderResponse>({
		endpoint: `/services-provider/${id}`,
		method: "GET",
	});
}

// ============================================
// GET ALL SERVICES BY PROVIDER
// ============================================
export function useServicesByProvider(providerId?: number, auto = true) {
	return useFetch<ServiceProviderResponse[]>({
		endpoint: `/services-provider/provider/${providerId ?? 0}`,
		method: "GET",
		auto: auto && typeof providerId === "number" && providerId > 0,
	});
}

// ============================================
// GET ACTIVE SERVICES BY PROVIDER
// ============================================
export function useActiveServicesByProvider(providerId?: number, auto = true) {
	return useFetch<ServiceProviderResponse[]>({
		endpoint: `/services-provider/provider/${providerId ?? 0}/active`,
		method: "GET",
		auto: auto && typeof providerId === "number" && providerId > 0,
	});
}

// ============================================
// CREATE SERVICE
// ============================================
export function useCreateServiceProvider() {
	const fetch = useFetch<ServiceProviderResponse, CreateServiceProviderPayload>({
		endpoint: "/services-provider",
		method: "POST",
		auto: false,
	});

	const create = (
		data: CreateServiceProviderPayload,
		files?: { pictures_service?: File[] },
	) => {
		return fetch.execute({ body: data, files });
	};

	return { ...fetch, create };
}

// ============================================
// UPDATE SERVICE
// ============================================
export function useUpdateServiceProvider(id: number) {
	const fetch = useFetch<ServiceProviderResponse, UpdateServiceProviderPayload>({
		endpoint: `/services-provider/${id}`,
		method: "PATCH",
		auto: false,
	});

	const update = (
		data: UpdateServiceProviderPayload,
		files?: { pictures_service?: File[] },
	) => {
		return fetch.execute({ body: data, files });
	};

	return { ...fetch, update };
}

// ============================================
// TOGGLE SERVICE ACTIVE STATUS
// ============================================
export function useToggleServiceActive(id?: number) {
	const fetch = useFetch<ServiceProviderResponse, ToggleServiceStatusPayload>({
		endpoint: `/services-provider/${id ?? 0}/toggle-active`,
		method: "PATCH",
		auto: false,
	});

	const toggle = (
		serviceIdOrIsActive: number | boolean,
		isActiveOverride?: boolean,
	) => {
		if (typeof serviceIdOrIsActive === "number") {
			if (typeof isActiveOverride !== "boolean") {
				return Promise.reject(
					new Error("Debes indicar el estado activo/inactivo del servicio"),
				);
			}

			return fetch.execute({
				endpoint: `/services-provider/${serviceIdOrIsActive}/toggle-active`,
				body: { isActive: isActiveOverride },
			});
		}

		if (typeof id !== "number" || id <= 0) {
			return Promise.reject(
				new Error("Se requiere el id del servicio para actualizar su estado"),
			);
		}

		return fetch.execute({
			endpoint: `/services-provider/${id}/toggle-active`,
			body: { isActive: serviceIdOrIsActive },
		});
	};

	return { ...fetch, toggle };
}

// ============================================
// DELETE SERVICE (Opcional)
// ============================================
export function useDeleteServiceProvider() {
	const fetch = useFetch<{ message: string }>({
		endpoint: "/services-provider",
		method: "DELETE",
		auto: false,
	});

	const remove = (id: number) => {
		return fetch.execute({ endpoint: `/services-provider/${id}` });
	};

	return { ...fetch, remove };
}
